/**
 * User Store Provider and Hooks
 *
 * 사용자 인증 및 프로필 관리를 위한 Zustand store 제공자와 훅들
 * - UserStoreProvider: 사용자 상태 관리를 위한 Context Provider
 * - useUserStore: 사용자 store에서 상태를 선택적으로 구독하는 훅
 *
 * 주요 사용 시나리오:
 * - 로그인 상태 확인: `const userInfo = useUserStore(store => store.info)`
 * - 로그인 처리: `const setUserInfo = useUserStore(store => store.setUserInfo)`
 * - 로그아웃 처리: `const clearUser = useUserStore(store => store.clearUserStore)`
 */

import { UserStore, createUserStore } from '@/stores/user/user-store';
import { createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';

/** createUserStore 함수에서 반환되는 store API 타입 */
type UserStoreApi = ReturnType<typeof createUserStore>;

/** 사용자 store를 공유하기 위한 React Context */
const UserStoreContext = createContext<UserStoreApi | null>(null);

/**
 * User Store Context Provider
 *
 * 애플리케이션 전체에서 사용자 인증 상태를 공유하기 위한 Context Provider
 * useRef를 사용하여 리렌더링 시에도 store 인스턴스가 유지되도록 보장
 *
 * localStorage 영속성으로 브라우저 새로고침 시에도 로그인 상태가 유지됨
 *
 * @param children - Provider로 감쌀 자식 컴포넌트들
 */
export const UserStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<UserStoreApi | null>(null);
  // store 인스턴스가 없을 때만 새로 생성 (싱글톤 패턴)
  storeRef.current ??= createUserStore();

  return <UserStoreContext.Provider value={storeRef.current}>{children}</UserStoreContext.Provider>;
};

/**
 * User Store Hook
 *
 * 사용자 store에서 필요한 상태나 액션만 선택적으로 구독하는 훅
 * selector 패턴을 통해 성능 최적화 (필요한 상태 변경 시에만 리렌더링)
 *
 * @template T - selector 함수의 반환 타입
 * @param selector - store에서 필요한 상태나 액션을 선택하는 함수
 * @returns selector 함수가 반환하는 값
 * @throws UserStoreProvider 외부에서 사용 시 에러 발생
 */
export const useUserStore = <T,>(selector: (store: UserStore) => T): T => {
  const userStoreContext = useContext(UserStoreContext);
  if (!userStoreContext) {
    throw new Error(`useUserStore must be used within UserStoreProvider`);
  }

  return useStore(userStoreContext, selector);
};
